import { forEach } from 'lodash'
import { Subscription } from 'Core/Subscription'
import { Studio } from 'Core/Studio'
import { SupportedPluginsKeyType } from 'Core/PluginCore/PluginsManager'
import { createReactElement, UpdatableReactElement } from 'shared/utils/react.utils'
import { ViolationsElement } from 'Layouts/Workbench/InformationElements/Violations/ViolationsElement'
import { ErrorAndWarningCount } from 'Types/Tasks.types'
import { ViolationType, ViolationsType, ViolationLevel } from 'shared/violations/types'

export type ControlledInputParams<T = unknown> = {
    type: string
    id: string
    ref?: React.RefObject<T>
    value?: string | string[] | number
    subscription?: Subscription<InputManagerSubscriptionTypes, InputManagerSubscriptionDataTypes>
    allowedPlugins?: SupportedPluginsKeyType[]
    addViolation?: (violation: ViolationType) => void
    removeViolation?: (violationId: string, level?: ViolationLevel) => void
}

type InputManagerSubscriptionDataTypes = {
    [InputManagerSubscriptionTypes.Value]: {
        input: ControlledInputParams
        value: string | string[] | number
    }
    [InputManagerSubscriptionTypes.Create]: {
        input: ControlledInputParams
        value: string | string[] | number
    }
    [InputManagerSubscriptionTypes.Update]: {
        input: ControlledInputParams
    }
}

export enum InputManagerSubscriptionTypes {
    Value = 'value',
    Create = 'create',
    Update = 'update',
}

export class InputManager {
    private controls: { [id: string]: ControlledInputParams } = {}
    private violations: ViolationsType = {
        error: {},
        warning: {},
    }
    private violationsUI?: UpdatableReactElement

    private listenersByType: {
        [controlType: string]: {
            eventType: InputManagerSubscriptionTypes
            listener: (
                data?: InputManagerSubscriptionDataTypes[InputManagerSubscriptionTypes]
            ) => void
        }[]
    } = {}

    constructor(private studio: Studio) {}

    initialize(options: { enableViolationUI: boolean }) {
        if (options.enableViolationUI) {
            this.addViolationUI()
        }
    }

    private addViolationUI() {
        this.violationsUI = createReactElement(ViolationsElement, {
            violations: this.violations,
            taskConfig: this.studio.taskConfig,
        })
        this.studio.workbench.enqueueUiUpdate(accessor =>
            accessor.toolbar.addInfoElement({
                element: this.violationsUI!.element,
                id: 'violations',
                alignment: 'center',
                placement: 'end',
            })
        )
    }

    registerInput(id: string, params: ControlledInputParams) {
        if (this.controls[id]) {
            return
        }
        this.controls[id] = {
            ...(this.controls[id] as ControlledInputParams),
            ...params,
            subscription: new Subscription<
                InputManagerSubscriptionTypes,
                InputManagerSubscriptionDataTypes
            >(),
        }
        if (this.listenersByType[params.type]) {
            forEach(this.listenersByType[params.type], listener => {
                this.controls[id].subscription?.subscribe(listener.eventType, listener.listener)
            })
        }
    }

    getViolation = (): ViolationsType => {
        return this.violations
    }

    getViolationCount = (level?: ViolationLevel): number => {
        if (level) {
            return Object.values(this.violations[level] || {}).length
        }

        const errorCount = Object.values(this.violations.error || {}).length
        const warningCount = Object.values(this.violations.warning || {}).length

        return errorCount + warningCount
    }

    checkViolationByID = (id: string): boolean => {
        return !!this.violations.error[id] || !!this.violations.warning[id]
    }

    addInputRef<T>(id: string, ref: React.RefObject<T>) {
        this.controls[id] ??= {} as ControlledInputParams<T>
        this.controls[id].ref = ref
        this.controls[id].subscription?.publishUpdate(InputManagerSubscriptionTypes.Create, {
            input: this.controls[id],
            value: this.controls[id].value ?? '',
        })
        this.controls[id].subscription?.publishUpdate(InputManagerSubscriptionTypes.Value, {
            input: this.controls[id],
            value: this.controls[id].value ?? '',
        })
    }

    updateControlValue = (id: string, value: string | string[] | number) => {
        this.controls[id].value = value
        this.controls[id].subscription?.publishUpdate(InputManagerSubscriptionTypes.Value, {
            input: this.controls[id],
            value,
        })
    }

    subscribeByType(
        controlType: string,
        listeners: {
            eventType: InputManagerSubscriptionTypes
            listener: (
                data?: InputManagerSubscriptionDataTypes[InputManagerSubscriptionTypes]
            ) => void
        }[]
    ) {
        this.listenersByType[controlType] ??= []
        this.listenersByType[controlType].push(...listeners)
        forEach(this.controls, control => {
            if (controlType === 'all' || control.type === controlType) {
                listeners.forEach(listener => {
                    control.subscription?.subscribe(listener.eventType, listener.listener)
                    if (['richtext', 'markdown'].includes(controlType)) {
                        control.subscription?.publishUpdate(InputManagerSubscriptionTypes.Create, {
                            input: control,
                            value: control.value ?? '',
                        })
                    }
                })
            }
        })
    }

    addViolation(violation: ViolationType) {
        this.violations[violation.type][violation.id] = violation
        this.updateControl()
        this.violationsUI?.updater.forceUpdate()
        this.studio.workbench.workbenchUiAccessor?.updateUI()
    }

    removeViolation(violationId: string, level?: ViolationLevel) {
        if (level) {
            delete this.violations[level][violationId]
        } else {
            forEach(this.violations, (violations, type) => {
                if (violations[violationId]) {
                    delete this.violations[type as ViolationLevel][violationId]
                }
            })
        }
        this.updateControl()
        this.violationsUI?.updater.forceUpdate()
        this.studio.workbench.workbenchUiAccessor?.updateUI()
    }

    getErrorAndWarningCount() {
        const errorAndWarningCount: ErrorAndWarningCount = {
            errorCount: Object.keys(this.getViolation().error).length,
            warningCount: Object.keys(this.getViolation().warning).length,
        }
        return errorAndWarningCount
    }

    updateControl() {
        forEach(this.controls, control => {
            control.subscription?.publishUpdate(InputManagerSubscriptionTypes.Update, {
                input: control,
            })
        })
    }
}
